/**
 * API client. The access token lives only in memory (never localStorage), so XSS cannot read a long-lived
 * credential; the refresh token is an HttpOnly cookie the script cannot see. On a 401 the client refreshes once
 * and retries. Refresh is serialised across tabs with the Web Locks API so two tabs never present the same
 * refresh token (which the server would treat as theft).
 */
export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string, public readonly data: any) { super(message); }
}

let accessToken: string | null = null;
let workingBranch: string | null = null;
/** The branch the user is working at; sent as X-Branch-Id on authenticated calls. Held in memory only. */
export const setWorkingBranch = (id: string | null) => { workingBranch = id; };
export const setAccessToken = (t: string | null) => { accessToken = t; };
export const hasAccessToken = () => accessToken !== null;

async function raw(path: string, o: { method?: string; body?: unknown; auth?: boolean; csrf?: boolean } = {}): Promise<any> {
  const headers: Record<string, string> = {};
  if (o.body !== undefined) headers["content-type"] = "application/json";
  if (o.auth && accessToken) headers["authorization"] = `Bearer ${accessToken}`;
  if (o.auth && workingBranch) headers["x-branch-id"] = workingBranch;
  if (o.csrf) headers["x-healthos-csrf"] = "1";
  let r: Response;
  try {
    r = await fetch(path, { method: o.method ?? (o.body !== undefined ? "POST" : "GET"), headers, credentials: "same-origin",
      ...(o.body !== undefined ? { body: JSON.stringify(o.body) } : {}) });
  } catch {
    throw new ApiError(0, "network", "Can't reach HealthOS. Check your internet connection and try again.", null);
  }
  const text = await r.text();
  const data = text ? JSON.parse(text) : null;
  if (!r.ok) throw new ApiError(r.status, data?.code ?? "error", data?.detail ?? "Something went wrong. Try again.", data);
  return data;
}

let inflight: Promise<boolean> | null = null;
export function refreshSession(): Promise<boolean> {
  const run = () => raw("/api/v1/auth/refresh", { method: "POST", csrf: true })
    .then((d) => { accessToken = d.accessToken; return true; })
    .catch(() => { accessToken = null; return false; });
  inflight ??= ((navigator as any).locks?.request("healthos-refresh", run) ?? run()).finally(() => { inflight = null; });
  return inflight!;
}

export const api = {
  /** Public endpoints (no token). */
  post: (path: string, body: unknown) => raw(path, { body }),
  get: (path: string) => raw(path),
  /** Authenticated: retries once after refreshing an expired access token. */
  async authed(path: string, o: { method?: string; body?: unknown } = {}): Promise<any> {
    try { return await raw(path, { ...o, auth: true }); }
    catch (e) {
      if (e instanceof ApiError && e.status === 401 && (await refreshSession())) return raw(path, { ...o, auth: true });
      throw e;
    }
  },
  async logout(): Promise<void> {
    try { await raw("/api/v1/auth/logout", { method: "POST", csrf: true }); } finally { accessToken = null; }
  },
};
